// src/components/watchlist/SubscriptionsList.js
import React, { useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Paper,
  Grid,
  Chip,
  IconButton,
  Avatar,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material'
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon,
  Draw as DrawIcon,
  LocationOn as LocationOnIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

const SubscriptionsList = ({
  loading,
  subscriptions,
  editingSubscriptionId,
  onToggleSubscription,
  onEditPrice,
  onCancelEdit,
  onUpdatePrice,
  onViewSubscription,
  onDeleteSubscription,
  onAddSubscription,
}) => {
  const { t } = useTranslation()

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  }

  // State for price editing
  const [editingMinPrice, setEditingMinPrice] = useState(null)
  const [editingMaxPrice, setEditingMaxPrice] = useState(null)

  // Set prices when editing a subscription
  React.useEffect(() => {
    if (editingSubscriptionId) {
      const subscription = subscriptions.find((s) => s.id === editingSubscriptionId)
      if (subscription) {
        setEditingMinPrice(subscription.min_price)
        setEditingMaxPrice(subscription.max_price)
      }
    } else {
      setEditingMinPrice(null)
      setEditingMaxPrice(null)
    }
  }, [editingSubscriptionId, subscriptions])

  // Handle updating subscription prices
  const handleSavePrice = async (id) => {
    const minPrice = parseInt(editingMinPrice, 10)
    const maxPrice = parseInt(editingMaxPrice, 10)

    const success = await onUpdatePrice(id, minPrice, maxPrice)
    if (success) {
      onCancelEdit()
    }
  }

  // Render a subscription card
  const renderSubscriptionCard = (subscription) => {
    const isEditing = editingSubscriptionId === subscription.id

    return (
      <motion.div
        variants={fadeIn}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.3 }}
        key={subscription.id}
      >
        <Card
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'all 0.2s ease',
            opacity: subscription.enabled ? 1 : 0.7,
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
            },
            overflow: 'visible',
            position: 'relative',
          }}
        >
          {subscription.type === 'rent' ? (
            <Chip
              size="small"
              label="RENT"
              color="primary"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                fontWeight: 'bold',
                zIndex: 1,
              }}
            />
          ) : (
            <Chip
              size="small"
              label="BUY"
              color="secondary"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                fontWeight: 'bold',
                zIndex: 1,
              }}
            />
          )}

          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor:
                        subscription.osm_data?.type === 'polygon'
                          ? 'secondary.light'
                          : 'primary.light',
                      width: 36,
                      height: 36,
                      mr: 1.5,
                    }}
                  >
                    {subscription.osm_data?.type === 'polygon' ? (
                      <DrawIcon fontSize="small" />
                    ) : (
                      <LocationOnIcon fontSize="small" />
                    )}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {subscription.address}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {subscription.postcode && `${subscription.postcode}, `}
                      {subscription.country}
                    </Typography>
                  </Box>
                </Box>

                {!isEditing && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight={500}>
                      {subscription.type === 'rent'
                        ? t('notifications.rentRange')
                        : t('notifications.priceRange')}
                      :{' '}
                      <strong>
                        €{subscription.min_price?.toLocaleString() || 0} - €
                        {subscription.max_price?.toLocaleString() || 0}
                        {subscription.type === 'rent' ? '/month' : ''}
                      </strong>
                    </Typography>
                  </Box>
                )}

                {isEditing && (
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={t('notifications.min')}
                          type="number"
                          value={editingMinPrice}
                          onChange={(e) => setEditingMinPrice(e.target.value)}
                          size="small"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={t('notifications.max')}
                          type="number"
                          value={editingMaxPrice}
                          onChange={(e) => setEditingMaxPrice(e.target.value)}
                          size="small"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>,
                          }}
                          error={parseInt(editingMinPrice, 10) > parseInt(editingMaxPrice, 10)}
                          helperText={
                            parseInt(editingMinPrice, 10) > parseInt(editingMaxPrice, 10)
                              ? t('notifications.minMustBeLessOrEqual')
                              : ''
                          }
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  <Chip
                    size="small"
                    label={subscription.count_of_listings}
                    color={subscription.count_of_listings > 0 ? 'success' : 'default'}
                    sx={{ mr: 1 }}
                  />

                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {t('notifications.createdAt')}:{' '}
                    {new Date(subscription.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    mt: { xs: 2, sm: 0 },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      display: 'flex',
                      justifyContent: { xs: 'space-between', sm: 'flex-end' },
                    }}
                  >
                    <Tooltip
                      title={
                        subscription.enabled
                          ? t('notifications.disable')
                          : t('notifications.enable')
                      }
                    >
                      <IconButton
                        onClick={() => onToggleSubscription(subscription.id, subscription.enabled)}
                        color={subscription.enabled ? 'primary' : 'default'}
                        sx={{ mr: 1 }}
                      >
                        {subscription.enabled ? (
                          <NotificationsActiveIcon />
                        ) : (
                          <NotificationsOffIcon />
                        )}
                      </IconButton>
                    </Tooltip>

                    {isEditing ? (
                      <Box sx={{ display: 'flex' }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleSavePrice(subscription.id)}
                          sx={{ mr: 1 }}
                          disabled={
                            parseInt(editingMinPrice, 10) > parseInt(editingMaxPrice, 10) ||
                            isNaN(parseInt(editingMinPrice, 10)) ||
                            isNaN(parseInt(editingMaxPrice, 10))
                          }
                        >
                          {t('notifications.save')}
                        </Button>
                        <Button variant="outlined" size="small" onClick={onCancelEdit}>
                          {t('notifications.cancel')}
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title={t('notifications.editPrice')}>
                          <IconButton
                            onClick={() => onEditPrice(subscription)}
                            color="secondary"
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t('notifications.delete')}>
                          <IconButton
                            onClick={() => onDeleteSubscription(subscription.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => onViewSubscription(subscription)}
                    sx={{ mt: 2, width: { xs: '100%', sm: 'auto' } }}
                  >
                    {t('notifications.view')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  // Render the empty state when no subscriptions exist
  const renderEmptyState = () => (
    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
      <NotificationsOffIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {t('notifications.noSubscriptions')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          'notifications.emptyMessage',
          "You haven't created any property alerts yet. Create your first alert to get notified about new listings."
        )}
      </Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={onAddSubscription}>
        {t('notifications.addSubscription')}
      </Button>
    </Paper>
  )

  return (
    <Box sx={{ mb: 3 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : subscriptions.length === 0 ? (
        renderEmptyState()
      ) : (
        <Box>
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {t('notifications.showing', 'Showing')} {subscriptions.length}{' '}
                {t('notifications.alert', 'alert', { count: subscriptions.length })}
              </Typography>
            </Grid>
          </Grid>

          {subscriptions.map((subscription) => renderSubscriptionCard(subscription))}
        </Box>
      )}
    </Box>
  )
}

export default SubscriptionsList
